import { faCircleNotch } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import triangleWarning from "assets/icons/triangle_exclamation.svg";

import { setPayableSession } from "actions/payableSessionActions";
import { ACTION } from "actions/paymentIntentActions";
import { setSelectedPayableTypeDefinition } from "actions/selectPayableTypeActions";
import { EMPTY_WALLET_ITEMS } from "actions/walletActions";
import { COMMON_NAMESPACE, PAYABLE_SELECT } from "constants/i18n-namespaces";
import { useFormik } from "formik";
import { LoadingButton, RadioRow, RadioRowGroup, Select } from "kubra-ux-forge";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import routing from "routing";
import { getLabelsWithKrn } from "selectors/labelsWithKrn";
import { IBiller } from "types/biller";
import { PayableType } from "types/payable";
import { getLabelWithKrn } from "utilities/helper";
import * as Yup from "yup";

export interface PayableTypeSelectFormProps {
  biller: IBiller;
}

const SelectPayableTypeForm = (props: PayableTypeSelectFormProps) => {
  const { biller } = props;
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation(PAYABLE_SELECT);

  const navigate = useNavigate();

  const labelSourceWithKrn = useSelector(getLabelsWithKrn);
  const dispatch = useDispatch();

  // Clear the session whenever we land on either the lookups page or payable type selection page.
  useEffect(() => {
    dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
    dispatch({ type: EMPTY_WALLET_ITEMS });
    dispatch(setPayableSession(undefined));

    // This effect only needs to run once on initial mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      biller.payableTypeDefinitions?.length === 1 &&
      (biller.payableTypeDefinitions[0].category.toLowerCase() === "account" ||
        biller.payableTypeDefinitions[0].category.toLowerCase() === "invoice")
    ) {
      // auto-selecting the only payable type available
      dispatch(
        setSelectedPayableTypeDefinition(biller.payableTypeDefinitions[0]),
      );
      // navigating to lookup directly when there is only one payable type
      navigate(routing.lookup.index());
    } else if (
      biller.payableTypeDefinitions?.length === 1 &&
      biller.payableTypeDefinitions[0].category.toLowerCase() === "freeform"
    ) {
      // auto-selecting the only payable type available
      dispatch(
        setSelectedPayableTypeDefinition(biller.payableTypeDefinitions[0]),
      );
      // navigating to lookup directly when there is only one payable type
      navigate(routing.payableAmount.index());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biller]);

  interface FormValues {
    selectPayableType: PayableType | null;
  }

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      selectPayableType: null,
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      selectPayableType: Yup.object().required(t("payable-type-required")),
    }),
    onSubmit: (values, actions) => {
      setLoading(true);

      if (values.selectPayableType) {
        dispatch(setSelectedPayableTypeDefinition(values.selectPayableType));

        if (
          values.selectPayableType.category.toLocaleLowerCase() === "freeform"
        ) {
          navigate(routing.payableAmount.index());
        } else if (
          values.selectPayableType.category.toLocaleLowerCase() === "order"
        ) {
          navigate(routing.payableCreateOrder.index());
        } else {
          navigate(routing.lookup.index());
        }
      }

      actions.resetForm();
      setSubmitting(false);
    },
  });

  const findPayableWithKrn = (krn: string) => {
    return biller.payableTypeDefinitions.find((payable) => payable.krn === krn);
  };

  const amountOfPayables = biller.payableTypeDefinitions?.length;

  const { errors, touched, setFieldValue, setSubmitting, handleSubmit } =
    formik;

  return (
    <>
      {errors?.selectPayableType && (
        <div className="validate-error">
          <img
            src={triangleWarning}
            alt={`${t("invalid-entry")}`}
            className="validate-error-icon"
          />
          <div>
            <div className="validation-error-header">{t("invalid-entry")}</div>
            <div className="validation-error-message">
              {t("payable-type-required")}
            </div>
          </div>
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        className="form-container"
        key={i18n.language}
      >
        <div className="payable-select-form heading-style-override">
          <h2 className="payable-select-info" style={{ paddingBottom: "6px" }}>
            {t("payable-select-info")}
          </h2>
          {(amountOfPayables > 6 || !amountOfPayables) && (
            <Select
              name="selectPayableType"
              isFilterable
              onChange={(option: { name: string; value: PayableType }[]) => {
                setFieldValue("selectPayableType", option[0]?.value);
              }}
              placeholder={t("select")}
              options={
                biller.payableTypeDefinitions?.map((payable) => ({
                  name: getLabelWithKrn(
                    labelSourceWithKrn.labelsWithKrn,
                    payable.typeKrn,
                    payable.name,
                  ),
                  value: payable,
                })) ?? []
              }
              hasError={
                !!(touched.selectPayableType && errors.selectPayableType)
              }
              canDeselect={false}
            />
          )}
          {amountOfPayables <= 6 && amountOfPayables > 0 && (
            <RadioRowGroup ariaLabel={t("payable-select-info")}>
              {biller.payableTypeDefinitions.map((payable) => (
                <RadioRow
                  key={payable.krn}
                  id={payable.krn}
                  selected={
                    formik.values.selectPayableType?.krn === payable.krn
                  }
                  label={getLabelWithKrn(
                    labelSourceWithKrn.labelsWithKrn,
                    payable.typeKrn,
                    payable.name,
                  )}
                  onSelect={() =>
                    setFieldValue(
                      "selectPayableType",
                      findPayableWithKrn(payable.krn),
                    )
                  }
                  hideIcon
                />
              ))}
            </RadioRowGroup>
          )}
        </div>

        <div className="select-form-continue-button">
          <LoadingButton
            loadingState="text"
            onClick={() => handleSubmit()}
            className="validate-button"
            type="submit"
            disabled={loading}
          >
            {loading && (
              <div className="button-icon">
                <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />
              </div>
            )}
            {!loading && <div>{t("continue", { ns: COMMON_NAMESPACE })}</div>}
          </LoadingButton>
        </div>
      </form>
    </>
  );
};

export default SelectPayableTypeForm;
